// src/admin/clientLimits.ts
/**
 * Member product-line limits (Trainer "Clients", spec 2026-10-29 §4.7 / §6). Pure helpers for the Users tab
 * column and the limit dialog.
 */
import type { AdminProductLine, AdminUser, ProductLineScope } from '../services/adminApi';
import { genericFailureMessage, readApiFailure } from './orgProvisioning';

export type ClientLimitMode = 'all' | 'limited';

/** The backend's MembershipProductLineScope.MaxLineIds: a limit names at most this many lines. */
export const MAX_LIMIT_LINES = 100;

/** Whose limit the dialog edits, bound to the organization it was opened in and to the open that made it. */
export interface ClientLimitTarget {
  orgId: string;
  user: AdminUser;
  /** Increases on every open and on every organization change; a completion for an older op is dropped. */
  op: number;
}

/** After a save or close the Users list is re-read; until it answers (or when it failed) rows may be stale. */
export type LimitRowsState = 'fresh' | 'refreshing' | 'failed';

/** The member to edit, only while the route is still on the organization the dialog was opened for. */
export function limitTargetUser(target: ClientLimitTarget | null, orgId: string | undefined): AdminUser | null {
  return target && target.orgId === orgId ? target.user : null;
}

/**
 * The target after a row's "Limit to" is activated: a new one only when no dialog is open, so a background
 * button reached by keyboard (the Modal has no focus trap) never swaps the member under an open dialog.
 */
export function openLimitTarget(
  current: ClientLimitTarget | null,
  orgId: string | undefined,
  user: AdminUser,
  op: number
): ClientLimitTarget | null {
  if (current || !orgId) return current;
  return { orgId, user, op };
}

/** True while the save or close of `target` may still touch the page: same organization, same open. */
export function isCurrentLimitOp(target: ClientLimitTarget, currentOrgId: string | undefined, currentOp: number): boolean {
  return target.orgId === currentOrgId && target.op === currentOp;
}

/** The lock after a Users read: an applied read is fresh; a failure locks only a refresh that was waiting on it. */
export function limitRowsAfterRead(rows: LimitRowsState, result: 'applied' | 'failed'): LimitRowsState {
  if (result === 'applied') return 'fresh';
  return rows === 'refreshing' ? 'failed' : rows;
}

/** The Users-tab notice, derived from the lock so the two always agree. */
export function limitRowsMessage(rows: LimitRowsState): string | null {
  return rows === 'failed' ? 'Could not refresh the user list. Reload the page before changing client limits.' : null;
}

/** A row's "Limit to" opens only from a fresh list, never for a Deleted member. */
export function canOpenLimit(user: AdminUser, rows: LimitRowsState): boolean {
  return rows === 'fresh' && user.status !== 'Deleted';
}

/** The list with `saved` in place of the row for the same organization and id (other rows untouched). */
export function replaceUser(users: AdminUser[], saved: AdminUser): AdminUser[] {
  return users.map((u) => (u.id === saved.id && u.organizationId === saved.organizationId ? saved : u));
}

export type UsersReadResult = 'applied' | 'superseded' | 'failed';

/**
 * Every Users-list read on the org detail page (first load, pagination, the refresh after a limit change) goes
 * through one sequence: only the latest read for the organization the page still shows may apply its rows or
 * report a failure; an earlier read is dropped, and a read for an organization the page has left is not sent.
 */
export function createUsersReader<T>(deps: {
  list: (orgId: string, skip: number) => Promise<T>;
  currentOrgId: () => string | undefined;
  onLoaded: (page: T) => void;
  onFailed: (err: unknown) => void;
}): { read: (orgId: string, skip: number) => Promise<UsersReadResult> } {
  let seq = 0;
  return {
    async read(orgId, skip) {
      // A read for an organization the page has left (a late mutation continuation) is never issued and never
      // takes a sequence number, so it cannot supersede the current organization's read.
      if (deps.currentOrgId() !== orgId) return 'superseded';
      const mine = ++seq;
      const current = () => mine === seq && deps.currentOrgId() === orgId;
      try {
        const page = await deps.list(orgId, skip);
        if (!current()) return 'superseded';
        deps.onLoaded(page);
        return 'applied';
      } catch (err) {
        if (!current()) return 'superseded';
        deps.onFailed(err);
        return 'failed';
      }
    },
  };
}

export interface PickableLine {
  id: string;
  name: string;
  depth: 0 | 1;
  archived: boolean;
  /** False for an archived, unselected parent kept only as the heading of a selected child. */
  selectable: boolean;
}

export function summarizeScope(scope: ProductLineScope | null | undefined): string {
  if (!scope || scope.mode === 'all') return 'All';
  if (scope.mode === 'limited') return `Limited (${scope.lineIds?.length ?? 0})`;
  return 'Invalid';
}

/** The org's top label as a plural for "All …" / "Only these …" (the API default is the singular "Product"). */
export function pluralLabel(label: string): string {
  return /s$/i.test(label) ? label : `${label}s`;
}

const byName = (a: AdminProductLine, b: AdminProductLine) => a.name.localeCompare(b.name);

/**
 * Lines to show in the picker: every live line, every selected line (so it can be removed), and the parent of
 * any shown child, kept as a non-selectable heading when it is archived and unselected. A child whose parent is
 * missing from the list is shown at the top level, so no selected known line is ever hidden.
 */
export function pickableLines(lines: AdminProductLine[], selected: ReadonlySet<string>): PickableLine[] {
  const byId = new Map(lines.map((l) => [l.id, l]));
  const shown = new Set(lines.filter((l) => !l.archived || selected.has(l.id)).map((l) => l.id));
  for (const id of [...shown]) {
    const parentId = byId.get(id)?.parentId;
    if (parentId && byId.has(parentId)) shown.add(parentId);
  }

  const row = (l: AdminProductLine, depth: 0 | 1): PickableLine => ({
    id: l.id,
    name: l.name,
    depth,
    archived: l.archived,
    selectable: !l.archived || selected.has(l.id),
  });
  const isTop = (l: AdminProductLine) => !l.parentId || !byId.has(l.parentId);
  const result: PickableLine[] = [];
  for (const top of lines.filter((l) => shown.has(l.id) && isTop(l)).sort(byName)) {
    result.push(row(top, 0));
    for (const child of lines.filter((l) => shown.has(l.id) && l.parentId === top.id).sort(byName)) {
      result.push(row(child, 1));
    }
  }
  return result;
}

export function unknownSelectedIds(lines: AdminProductLine[], selected: ReadonlySet<string>): string[] {
  const known = new Set(lines.map((l) => l.id));
  return [...selected].filter((id) => !known.has(id)).sort();
}

export function scopeRequest(
  mode: ClientLimitMode,
  selected: ReadonlySet<string>,
  loadedVersion?: number
): { mode: ClientLimitMode; lineIds: string[] | null; productLineScopeVersion?: number } {
  const scope = mode === 'all' ? { mode, lineIds: null } : { mode, lineIds: [...selected].sort() };
  return typeof loadedVersion === 'number' ? { ...scope, productLineScopeVersion: loadedVersion } : scope;
}

export function mapScopeUpdateFailure(err: unknown): string {
  const { status, code, detail, data } = readApiFailure(err);
  const generic = genericFailureMessage(status, code);
  if (generic) return generic;
  const apiCode = data['code'] ?? data['Code'];
  if (status === 404) return 'That person is no longer a member of this organization.';
  if (status === 503 && apiCode === 'product_line_scope_writes_disabled') {
    return 'Client limits are not switched on in this environment yet.';
  }
  if (status === 403) {
    return apiCode === 'product_line_scope_self_write_forbidden'
      ? 'You cannot change your own limit. Ask another admin to do it.'
      : 'You do not have permission to change this person’s limit.';
  }
  if (status === 409 && apiCode === 'product_line_scope_owner_protected') {
    return 'Owners can’t be limited: they manage every client. The organization’s Owner can change this person’s role from the Team page first.';
  }
  if (status === 409 && apiCode === 'product_line_scope_last_unrestricted_admin') {
    return 'This is the only owner or admin with access to every client. Give another owner or admin full access before limiting this one.';
  }
  if (status === 409 && apiCode === 'product_line_scope_version_conflict') {
    return 'This person’s limit changed while you were editing it. Close this and try again.';
  }
  if (status === 409) return 'Someone changed this membership at the same moment. Close this and try again.';
  if (status === 400) return detail || 'Pick at least one, or choose All.';
  return detail || 'Failed to save the limit.';
}
